import * as am4core from '@amcharts/amcharts4/core';
import {
    RadarChart,
    ValueAxis,
    CategoryAxis,
    AxisRendererRadial,
    RadarColumnSeries
} from '@amcharts/amcharts4/charts';

import { ChartModel } from '../chart.model';
import { GaugeChartOptionsModel } from './gauge-chart-options.model';
import { DataPointModel } from '../data-point.model';
import { chartColors } from '../../enums/chart-colors.enum';
import { LabelOptions } from '../series-label-options.model';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';

export class GaugeChartModel extends ChartModel {
    readonly singleSeries: boolean = true;

    readonly percentLabelDefaultOptions: LabelOptions = {
        fontSize: 40,
        fontWeight: '200',
        fill: userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.bigNumberColor as chartColors,
        horizontalCenter: 'middle',
        verticalCenter: 'middle',
        dy: -20,
        dx: 2
    };

    readonly seriesLabelDefaultOptions: LabelOptions = {
        fontSize: 14,
        fill: chartColors.agprBlack,
        horizontalCenter: 'middle',
        verticalCenter: 'middle',
        dy: 9
    };

    declare amchart: RadarChart;
    declare options: GaugeChartOptionsModel;
    xAxis: ValueAxis;
    percentLabel: am4core.Label;
    seriesLabel: am4core.Label;

    /**
     * Radar chart acting as a gauge chart
     */
    public initChart(element: HTMLElement): void {
        this.element = element;
        this.amchart = am4core.create(this.element, RadarChart);

        if (this.options.minXDegrees) {
            this.amchart.startAngle = this.options.minXDegrees;
        } else {
            this.amchart.startAngle = -180;
        }

        if (this.options.maxXDegrees) {
            this.amchart.endAngle = this.options.maxXDegrees;
        } else {
            this.amchart.endAngle = 0;
        }

        this.amchart.innerRadius = am4core.percent(this.options.innerRadiusPercentage || 84);
        this.amchart.padding(0, 0, 0, 0);

        const categoryAxis = this.amchart.yAxes.push(new CategoryAxis<AxisRendererRadial>());
        categoryAxis.dataFields.category = 'category';
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.grid.template.strokeOpacity = 0;

        this.xAxis = this.amchart.xAxes.push(new ValueAxis());
        this.xAxis.renderer.grid.template.strokeOpacity = 0;
        this.xAxis.min = 0;
        this.xAxis.max = 100;
        this.xAxis.strictMinMax = true;
        this.xAxis.renderer.ticks.template.disabled = true;
        this.xAxis.renderer.labels.template.disabled = true;

        const percentLabelStyles: LabelOptions = Object.assign(
            this.percentLabelDefaultOptions,
            this.options.percentLabelOptions
        );

        const seriesLabelStyles: LabelOptions = Object.assign(
            this.seriesLabelDefaultOptions,
            this.options.seriesLabelOptions
        );

        this.percentLabel = this.amchart.radarContainer.createChild(am4core.Label);
        this.percentLabel.fontSize = percentLabelStyles.fontSize;
        this.percentLabel.fontWeight = percentLabelStyles.fontWeight;
        this.percentLabel.fill = am4core.color(percentLabelStyles.fill);
        this.percentLabel.horizontalCenter = percentLabelStyles.horizontalCenter;
        this.percentLabel.verticalCenter = percentLabelStyles.verticalCenter;
        this.percentLabel.dy = percentLabelStyles.dy;
        this.percentLabel.dx = percentLabelStyles.dx;

        this.seriesLabel = this.amchart.radarContainer.createChild(am4core.Label);
        this.seriesLabel.fontSize = seriesLabelStyles.fontSize;
        this.seriesLabel.fill = am4core.color(seriesLabelStyles.fill);
        this.seriesLabel.textAlign = seriesLabelStyles.textAlign;
        this.seriesLabel.horizontalCenter = seriesLabelStyles.horizontalCenter;
        this.seriesLabel.verticalCenter = seriesLabelStyles.verticalCenter;
        this.seriesLabel.dy = seriesLabelStyles.dy;
        this.seriesLabel.tooltip.dy = seriesLabelStyles.tooltipDy;
        this.seriesLabel.tooltip.pointerOrientation = seriesLabelStyles.tooltipPointerOrientation;
        this.seriesLabel.tooltipText = seriesLabelStyles.tooltip;
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);

        this.amchart.scrollbarX.visible = false;
        this.amchart.zoomOutButton.visible = false;
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        super.unsetExportStyles();

        this.amchart.zoomOutButton.visible = true;
        this.amchart.scrollbarX.visible = true;
    }

    /**
     * Updates the current presentation of data in the chart
     */
    updateData(data: DataPointModel[][]): void {
        this.rawData = data;
        if (!data || !data[0] || !data[0][0] && !this.options.notAvailable) {
            this.amchart.data = null;
            this.amchart.series.clear();
            return;
        }

        const background = this.amchart.series.push(new RadarColumnSeries());
        background.dataFields.categoryY = 'category';
        background.dataFields.valueX = 'full';
        background.clustered = false;

        background.columns.template.fill = am4core.color(chartColors.agprMediumGrey);
        background.columns.template.fillOpacity = 0.08;
        background.columns.template.strokeWidth = 0;
        background.columns.template.radarColumn.cornerRadius = 50;
        background.scale = 1.15;

        const series1 = this.amchart.series.push(new RadarColumnSeries());
        series1.dataFields.categoryY = 'category';
        series1.dataFields.valueX = 'xValue';
        series1.clustered = false;
        series1.columns.template.strokeWidth = 0;
        series1.columns.template.radarColumn.cornerRadius = 50;
        series1.scale = 1.15;

        if (this.options.notAvailable) {
            this.amchart.data = [{
                'xValue': 'N/A',
                'full': 100,
                // Category is required or the chart won't render, doesn't have to have anything
                'category': ''
            }];
            this.percentLabel.text = 'N/A';
            this.percentLabel.fill = am4core.color(chartColors.agprMediumGrey);
            this.percentLabel.dy = -5;
            return;
        }

        if (isNaN(data[0][0].xValue)) {
            this.amchart.data = [{
                'xValue': 'N/A',
                'full': 100,
                // Category is required or the chart won't render, doesn't have to have anything
                'category': ''
            }];
            this.percentLabel.fontSize = '1.25rem';
            this.percentLabel.text = '-- %';
            this.percentLabel.fill = am4core.color(chartColors.agprMediumGrey);
            this.percentLabel.dy = -5;
            this.percentLabel.dx = 0;
            return;
        }

        const dataPoint: DataPointModel = data[0][0];
        this.amchart.data = [{
            'xValue': data[0][0].xValue,
            'full': 100,
            // Category is required or the chart won't render, doesn't have to have anything
            'category': ''
        }];

        if (!this.options.useOneColor) {
            if (dataPoint.xValue < 75) {
                series1.columns.template.fill = am4core.color(this.defaultChartColors.gaugeColors.color1);
            } else if (dataPoint.xValue < 100) {
                series1.columns.template.fill = am4core.color(this.defaultChartColors.gaugeColors.color2);
            } else {
                series1.columns.template.fill = am4core.color(this.defaultChartColors.gaugeColors.color3);
            }
        } else {
            series1.columns.template.fill = am4core.color(this.defaultChartColors.gaugeColors.color4 ?? this.defaultChartColors.gaugeColors.color1);
            this.percentLabel.fill = am4core.color(chartColors.agprText);
        }

        this.percentLabel.fontSize = this.options.percentageFontSize || 40;
        this.percentLabel.fontWeight = this.options.percentageFontWeight as am4core.FontWeight || '600';
        const percentageRatio = 28 / 40;
        const percentageSize = this.percentLabel.fontSize * percentageRatio;
        this.percentLabel.text = Math.round(dataPoint.xValue * 10) / 10 + '[font-size:' + percentageSize + 'px]%[/]';

        if (dataPoint.label) {
            this.seriesLabel.text = dataPoint.label;
            this.percentLabel.dy = this.percentLabel.dy || -20;
        } else {
            this.percentLabel.dy = -5;
        }

    }

    /**
     * Required but not currently used
     */
    updateSeriesStyles(): void {}

    /**
     * Exports data using amcharts
     */
    export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {

        return this.amchart.exporting.export(type);
    }

    public isContentEmpty(): boolean {
        return false;
    }
}
